import { z } from "zod";

import { TrtConfigFileSchema } from "../lib/model-engine-config-file.models";
import { RoutingRuleApiSchema } from "../lib/routing-rule.models";

// Shared record schemas for the deployment endpoints, kept in the leaf models
// package so routers can `.output()` them without a service → models cycle.
// Mirrors `DeploymentListItem` from `@inference-net/deployment-service`; each
// consuming procedure's `.output()` makes tsc enforce the match.

export const deploymentLifecycleStateSchema = z.enum([
  "Active",
  "Archived",
  "Deleted",
]);

export const deploymentRolloutStrategySchema = z.enum([
  "create-before-destroy",
  "destroy-before-create",
]);

export const deploymentPoolRoleSchema = z.enum([
  "standard",
  "uncached_prefill",
]);

/** `endpoints.tower_affinity_hint`: ATC spreads or groups an endpoint's instances across towers. */
export const towerAffinityHintSchema = z.enum(["spread", "together"]);

export type TowerAffinityHint = z.infer<typeof towerAffinityHintSchema>;

export const deploymentStatusSchema = z.enum([
  "Pending",
  "Active",
  "Degraded",
  "Updating",
  "Stopped",
  "Unschedulable",
]);

/** Instance-requirements config (`DeploymentInstanceRequirements` jsonb). */
export const deploymentInstanceRequirementsSchema = z.object({
  card_options: z.array(z.object({ type: z.string(), count: z.number() })),
  host_ram_gib: z.number().optional(),
});

/**
 * The config columns on the deployment row. `configHash` identifies the
 * instance-affecting config: instances stamped with an equal hash are on the
 * desired config. The override maps are the caller's own team-scoped config,
 * echoed back unchanged. `modelId`/`instanceRequirements` are null only on a
 * deployment that was never configured.
 */
const deploymentConfigColumnsSchema = z.object({
  modelId: z.string().nullable(),
  desiredInstanceCount: z.number(),
  instanceRequirements: deploymentInstanceRequirementsSchema.nullable(),
  modelConfigIdOverride: z.string().nullable(),
  modelConfigVersionOverride: z.number().nullable(),
  modelConfigFlagOverrides: z.record(z.string(), z.string()).nullable(),
  modelConfigEnvironmentVariableOverrides: z
    .record(z.string(), z.string())
    .nullable(),
  modelConfigConfigFileOverrides: z.array(TrtConfigFileSchema).nullable(),
  modelConfigParallelismOverride: z.number().nullable(),
  instanceRoutingEngineShardConfigOverride: z
    .array(z.object({ index: z.number(), max_reqs: z.number() }))
    .nullable(),
  cycleNonce: z.number(),
  isTrusted: z.boolean(),
  configHash: z.string(),
});

export const deploymentListItemSchema = deploymentConfigColumnsSchema.extend({
  id: z.string(),
  name: z.string(),
  lifecycleState: deploymentLifecycleStateSchema,
  status: deploymentStatusSchema,
  /** The endpoint serving this deployment; the fields below it on this row are the endpoint's. */
  endpointId: z.string(),
  endpointName: z.string(),
  publicModelIdentifier: z.string(),
  teamId: z.string(),
  projectId: z.string(),
  isServerlessDeployment: z.boolean(),
  serverlessCostPerMillionIn: z.number().nullable(),
  serverlessCostPerMillionCachedIn: z.number().nullable(),
  serverlessCostPerMillionOut: z.number().nullable(),
  // Defaulted so rows cached before these columns existed still validate.
  serverlessInputNanocreditsPerMillion: z.number().nullable().default(null),
  serverlessCachedInputNanocreditsPerMillion: z
    .number()
    .nullable()
    .default(null),
  serverlessOutputNanocreditsPerMillion: z.number().nullable().default(null),
  billingExempt: z.boolean(),
  gpuHourCostOverrideCents: z.number().nullable(),
  queueTimeoutMs: z.number().nullable(),
  totalReqTimeoutMs: z.number(),
  timeToNextTokenTimeoutMs: z.number().nullable(),
  maxQueueSize: z.number().nullable(),
  chunkBufferTimeoutMs: z.number().nullable(),
  missingChunkCacheTtlMs: z.number().nullable(),
  replayBufferMaxChunks: z.number().nullable(),
  enableStickyBlockKeys: z.boolean(),
  towerAffinityHint: towerAffinityHintSchema,
  enableIncrementalUsageBilling: z.boolean(),
  /** Rank within the endpoint (1 wins); null until the endpoint's routing is saved. */
  routingRank: z.number().int().nullable(),
  /** Eligibility rule; null means always eligible. */
  routingRule: RoutingRuleApiSchema.nullable(),
  isDefaultDeployment: z.boolean(),
  dockerImage: z.string().nullable(),
  dockerImageTag: z.string().nullable(),
  rolloutStrategy: deploymentRolloutStrategySchema,
  rolloutBatchSize: z.number(),
  poolRole: deploymentPoolRoleSchema,
  overrideDesiredCliVersion: z.string().nullable(),
  isManualDeployment: z.boolean(),
  createdByUserId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable(),
  allowedTeamIds: z.array(z.string()),
  acceptedControlTowerIds: z.array(z.string()),
  createdByUser: z
    .object({
      id: z.string(),
      name: z.string(),
      email: z.string(),
      image: z.string().nullable(),
    })
    .nullable(),
  model: z
    .object({
      id: z.string(),
      name: z.string(),
      canonicalAlias: z.string(),
    })
    .nullable(),
});

export const deploymentListSchema = z.object({
  items: z.array(deploymentListItemSchema),
  total: z.number(),
});

// Base deployment row (`DeploymentRow`: the deployment joined to its endpoint), without the
// list-item join extras. This is what `createDeployment`/`updateDeployment`
// return for the `deployment` field.
export const deploymentRowSchema = deploymentConfigColumnsSchema.extend({
  id: z.string(),
  name: z.string(),
  lifecycleState: deploymentLifecycleStateSchema,
  status: deploymentStatusSchema,
  /** The endpoint serving this deployment; the fields below it on this row are the endpoint's. */
  endpointId: z.string(),
  endpointName: z.string(),
  publicModelIdentifier: z.string(),
  teamId: z.string(),
  projectId: z.string(),
  isServerlessDeployment: z.boolean(),
  serverlessCostPerMillionIn: z.number().nullable(),
  serverlessCostPerMillionCachedIn: z.number().nullable(),
  serverlessCostPerMillionOut: z.number().nullable(),
  // Defaulted so rows cached before these columns existed still validate.
  serverlessInputNanocreditsPerMillion: z.number().nullable().default(null),
  serverlessCachedInputNanocreditsPerMillion: z
    .number()
    .nullable()
    .default(null),
  serverlessOutputNanocreditsPerMillion: z.number().nullable().default(null),
  billingExempt: z.boolean(),
  gpuHourCostOverrideCents: z.number().nullable(),
  queueTimeoutMs: z.number().nullable(),
  totalReqTimeoutMs: z.number(),
  timeToNextTokenTimeoutMs: z.number().nullable(),
  maxQueueSize: z.number().nullable(),
  chunkBufferTimeoutMs: z.number().nullable(),
  missingChunkCacheTtlMs: z.number().nullable(),
  replayBufferMaxChunks: z.number().nullable(),
  enableStickyBlockKeys: z.boolean(),
  /** Defaulted for rows cached before it existed. */
  towerAffinityHint: towerAffinityHintSchema.default("spread"),
  enableIncrementalUsageBilling: z.boolean(),
  /** Rank within the endpoint (1 wins); null until the endpoint's routing is saved. */
  routingRank: z.number().int().nullable(),
  /** Eligibility rule; null means always eligible. */
  routingRule: RoutingRuleApiSchema.nullable(),
  isDefaultDeployment: z.boolean(),
  dockerImage: z.string().nullable(),
  dockerImageTag: z.string().nullable(),
  rolloutStrategy: deploymentRolloutStrategySchema,
  rolloutBatchSize: z.number(),
  /** Defaulted for rows cached before it existed. */
  poolRole: deploymentPoolRoleSchema.default("standard"),
  overrideDesiredCliVersion: z.string().nullable(),
  isManualDeployment: z.boolean(),
  createdByUserId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable(),
});

/** `createDeployment`/`updateDeployment`/`cycleAllInstances` return the deployment. */
export const deploymentMutationResultSchema = z.object({
  deployment: deploymentRowSchema,
});

/**
 * One `deployment_events` row: an entry in a deployment's operational history.
 *
 * `actorType` and `eventType` are open strings, not enums — a producer may
 * write a value this reader has never heard of and the response must still
 * validate. `payload` is passed through exactly as stored: no per-type
 * parsing, no key stripping, arbitrary nesting preserved.
 */
export const deploymentEventSchema = z.object({
  id: z.string(),
  deploymentId: z.string(),
  deploymentInstanceId: z.string().nullable(),
  actorType: z.string(),
  actorUserId: z.string().nullable(),
  eventType: z.string(),
  eventVersion: z.number(),
  payload: z.record(z.string(), z.unknown()),
  occurredAt: z.date(),
  createdAt: z.date(),
});

export type DeploymentEvent = z.infer<typeof deploymentEventSchema>;

/**
 * Keyset cursor into the event timeline. `(createdAt, id)` is the sort key, so
 * the next page is every tuple strictly less than this one — stable across
 * events sharing a `createdAt`.
 */
export const deploymentEventCursorSchema = z.object({
  createdAt: z.date(),
  id: z.string(),
});

export type DeploymentEventCursor = z.infer<typeof deploymentEventCursorSchema>;

export const deploymentEventsPageSchema = z.object({
  items: z.array(deploymentEventSchema),
  nextCursor: deploymentEventCursorSchema.nullable(),
});

export type DeploymentEventsPage = z.infer<typeof deploymentEventsPageSchema>;

/**
 * Bounds on an event timeline's `createdAt` (the sort key): `since` is
 * inclusive, `until` exclusive.
 */
export type EventTimeWindow = {
  since?: Date;
  until?: Date;
};

/** Filters accepted by the event timeline read; combined with AND. */
export type DeploymentEventQuery = EventTimeWindow & {
  deploymentId: string;
  actorType?: string;
  eventType?: string;
  deploymentInstanceId?: string;
  pageSize: number;
  cursor?: DeploymentEventCursor;
};

/**
 * One `endpoint_events` row: an entry in an endpoint's history (creation,
 * deployments added, settings changes with before/after values, routing
 * changes with every deployment's rank and rule before and after, aliases).
 * Open strings and pass-through payload, as for deployment events.
 */
export const endpointEventSchema = z.object({
  id: z.string(),
  endpointId: z.string(),
  actorType: z.string(),
  actorUserId: z.string().nullable(),
  eventType: z.string(),
  eventVersion: z.number(),
  payload: z.record(z.string(), z.unknown()),
  occurredAt: z.date(),
  createdAt: z.date(),
});

export type EndpointEventRecord = z.infer<typeof endpointEventSchema>;

export const endpointEventsPageSchema = z.object({
  items: z.array(endpointEventSchema),
  nextCursor: deploymentEventCursorSchema.nullable(),
});

export type EndpointEventsPage = z.infer<typeof endpointEventsPageSchema>;

/** Filters accepted by the endpoint timeline read; combined with AND. */
export type EndpointEventQuery = EventTimeWindow & {
  endpointId: string;
  actorType?: string;
  eventType?: string;
  pageSize: number;
  cursor?: DeploymentEventCursor;
};
